interface Task {
  id: string;
  title: string;
  description: string;
  status: 'Pending' | 'In Progress' | 'Done' | 'Blocked';
  dueDate: string;
  createdAt: string;
}

interface TasksListProps {
  tasks: Task[];
}

export default function TasksList({ tasks }: TasksListProps) {
  const getStatusBadge = (status: Task['status']) => {
    const statusConfig = {
      'Pending': { bg: 'bg-gray-100', text: 'text-gray-700', icon: 'ri-time-line' },
      'In Progress': { bg: 'bg-blue-100', text: 'text-blue-700', icon: 'ri-loader-4-line' },
      'Done': { bg: 'bg-green-100', text: 'text-green-700', icon: 'ri-checkbox-circle-line' },
      'Blocked': { bg: 'bg-red-100', text: 'text-red-700', icon: 'ri-error-warning-line' }
    };
    const config = statusConfig[status];
    return (
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 ${config.bg} ${config.text} text-xs font-medium rounded-full`}>
        <i className={`${config.icon} text-sm`}></i>
        <span>{status}</span>
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (tasks.length === 0) {
    return (
      <div className="text-center py-8">
        <i className="ri-task-line text-4xl text-gray-300 mb-3"></i>
        <p className="text-sm text-gray-500">No tasks assigned yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tasks.map(task => (
        <div
          key={task.id}
          className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-gray-300 transition-colors"
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <h4 className="text-sm font-semibold text-gray-900 flex-1">
              {task.title}
            </h4>
            {getStatusBadge(task.status)}
          </div>
          
          {task.description && (
            <p className="text-sm text-gray-600 mb-3">
              {task.description}
            </p>
          )}
          
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <div className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              <span>Created: {formatDate(task.createdAt)}</span>
            </div>
            {task.dueDate && (
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-check-line"></i>
                <span>Due: {formatDate(task.dueDate)}</span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}